import axios, { AxiosResponse } from 'axios';
import { Dispatch } from 'redux';
import { datadogLogs } from '@datadog/browser-logs';
import SITE_CONFIG_TYPES from './types';
import { ENV_CONFIG } from 'config/environment';
import API_ROUTES from 'services/apiRoutes';
import { noop } from 'helpers/utils';
import { siteConfigHelper } from './helper';

const headers = {
  'Content-Type': 'application/json',
  'Access-Control-Allow-Origin': '*',
};

// The Header and the page (e.g. FormContent) both request it on mount; share one in-flight request per URL.
let pendingSiteConfigRequest: { url: string; promise: Promise<any> } | null = null;

class UnexpectedSiteConfigShapeError extends Error {
  response: AxiosResponse;

  constructor(response: AxiosResponse) {
    super('Unexpected site config response shape');
    this.name = 'UnexpectedSiteConfigShapeError';
    this.response = response;
  }
}

// Only the backend correlation id is taken from error bodies; the rest may be third-party content.
const getDebuggingId = (body: unknown): string | undefined => {
  if (body && typeof body === 'object' && 'debugging_id' in body) return String(body.debugging_id);
  if (typeof body === 'string') return body.match(/debugging_id: (\S+)/)?.[1];
  return undefined;
};

// A plain copy: the original carries the response and request config (body, API key header).
const toLoggedError = (error: any): Error => {
  const logged = new Error(error instanceof Error ? error.message : String(error));
  if (error instanceof Error) {
    logged.name = error.name;
    logged.stack = error.stack;
  }
  return logged;
};

const getFailureContext = (error: any, url: string) => {
  // Base needed: local dev's API base URL is relative (/backend).
  const sections = new URL(url, window.location.origin).searchParams.getAll('sections');
  if (error instanceof UnexpectedSiteConfigShapeError) {
    const { data, status, headers: responseHeaders } = error.response;
    return {
      failure: 'unexpected_shape',
      sections,
      response_status: status,
      response_type: data === null ? 'null' : typeof data,
      response_content_type: responseHeaders?.['content-type'],
      response_length: typeof data === 'string' ? data.length : undefined,
    };
  }
  return {
    failure: 'request_error',
    sections,
    response_status: error?.response?.status,
    error_code: error?.code,
    debugging_id: getDebuggingId(error?.response?.data),
  };
};

const requestSiteConfig = async (dispatch: Dispatch, url: string): Promise<any> => {
  dispatch(SITE_CONFIG_TYPES.siteConfigStart());

  try {
    const response = await axios.get(url, {
      headers: { ...headers, 'X-API-Key': process.env.REACT_APP_API_KEY },
    });
    // The API only ever returns a list; anything else didn't come from our API.
    if (!Array.isArray(response.data)) {
      throw new UnexpectedSiteConfigShapeError(response);
    }
    dispatch(SITE_CONFIG_TYPES.siteConfigSuccess(response.data));
    return response;
  } catch (error: any) {
    dispatch(SITE_CONFIG_TYPES.siteConfigError(error.message));
    try {
      datadogLogs.logger.warn(
        'Site config request failed',
        getFailureContext(error, url),
        toLoggedError(error)
      );
    } catch {
      noop();
    }
    throw error;
  }
};

export const getSiteConfig =
  () =>
  (dispatch: Dispatch): Promise<any> => {
    const { urlSection } = siteConfigHelper();
    const url = `${ENV_CONFIG().API.BASE_URL}${API_ROUTES.GET_SITE_CONFIG(urlSection)}`;

    if (pendingSiteConfigRequest?.url !== url) {
      const promise = requestSiteConfig(dispatch, url).finally(() => {
        if (pendingSiteConfigRequest?.promise === promise) pendingSiteConfigRequest = null;
      });
      pendingSiteConfigRequest = { url, promise };
    }

    return pendingSiteConfigRequest.promise;
  };
